import {
  RouteLegDTO,
  RoutePackageDTO,
  RouteRebookingKindEnum,
  RouteRebookingProposalDTO,
} from 'api/model';

export type RebookingAnswer =
  | { kind: 'confirm'; newBookedTo: Date }
  | { kind: 'decline' };

/**
 * Ett ja som redan är sparat. `ConfirmRebooking` flyttar `BookedTo` i databasen direkt, och
 * omräkningen bär inga förslag — vyn minns svaret själv så att benet kan visas som bekräftat.
 */
export interface ConfirmedRebooking {
  assignmentId: number;
  oldBookedTo: Date;
  newBookedTo: Date;
  confirmedAt: Date;
}

/** "HH:mm" i den bokade tidens dag → ny tid samma dag. */
export const atClockOnSameDay = (day: Date, clock: string): Date | undefined => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock);
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;
  const result = new Date(day);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const formatDayHours = (hours: number): string =>
  hours.toFixed(1).replace('.', ',');

/** Numret trafikledaren ska ringa först, och det andra som reserv. */
export const rebookingContacts = (
  r: RouteRebookingProposalDTO
): { primary?: RebookingContact; secondary?: RebookingContact } => {
  const customerPhone = r.customerPhoneNumber.trim();
  const bookerPhone = r.bookerPhoneNumber.trim();
  const customer: RebookingContact | undefined = customerPhone
    ? { role: 'Kund', name: r.customerName, phone: customerPhone }
    : undefined;
  const booker: RebookingContact | undefined = bookerPhone
    ? {
        role: 'Bokare',
        name: r.bookerName,
        phone: bookerPhone,
        company: r.customerCompanyName,
      }
    : undefined;
  const [first, second] = r.callCustomer ? [customer, booker] : [booker, customer];
  return first ? { primary: first, secondary: second } : { primary: second };
};

export interface RebookingContact {
  role: 'Kund' | 'Bokare';
  name?: string;
  phone: string;
  company?: string;
}

export type RebookingBarTone = 'own' | 'donor' | 'moved';

export interface RebookingBar {
  assignmentId: number;
  /** Benets nummer i kortet och på kartan. */
  legNumber: number;
  start: number;
  end: number;
  tone: RebookingBarTone;
}

export interface RebookingTimeline {
  before: RebookingBar[];
  after: RebookingBar[];
  /** Flyttade ben på den tid som står i dag, i raden "Om ja". */
  ghosts: { start: number; end: number }[];
  hoursBefore: number;
  hoursAfter: number;
  /** Midnatt före första benets planerade start. Axelns timmar räknas härifrån. */
  dayStartMs: number;
  /** Timmar från dayStartMs; kan vara negativa eller över 24 när dagen går över midnatt. */
  axisStartHour: number;
  axisEndHour: number;
  /** Timmar mellan axelns etiketter. Går jämnt upp i axelns längd. */
  tickStepHours: number;
}

const shiftMs = (r: RouteRebookingProposalDTO) => r.shiftMinutes * 60_000;

const legSpan = (leg: RouteLegDTO) => ({
  start: new Date(leg.plannedStart).getTime(),
  end: new Date(leg.plannedEnd).getTime(),
});

/**
 * Dagen före och efter flytten, som två rader staplar. En skiss, inte en omräkning: benen ligger
 * på paketets planerade tider och de flyttade förskjuts med flytten.
 * - Fyll kort dag: paketet är räknat på den nya tiden. "Nu" är mottagarens egna ben, "Om ja" hela
 *   paketet med givarens ben och det flyttade benet utmärkta.
 * - Håltimme: paketet är räknat på dagens tid. "Nu" är paketet, "Om ja" har de flyttade benen
 *   förskjutna.
 * Antalet timmar kommer från förslaget, inte från staplarna.
 */
export const buildRebookingTimeline = (
  pkg: RoutePackageDTO
): RebookingTimeline | undefined => {
  const proposals = pkg.rebookingProposals ?? [];
  if (proposals.length === 0 || pkg.legs.length === 0) return undefined;

  const byAssignment = new Map(proposals.map((r) => [r.assignmentId, r]));
  // Taket på arbetsdagen ritas som en ifylld dag: paketet är räknat på den nya tiden.
  const fill = proposals.find(
    (r) =>
      r.kind === RouteRebookingKindEnum.FillShortDay ||
      r.kind === RouteRebookingKindEnum.FitWithinDayCap
  );
  const legs = pkg.legs.map((leg, index) => ({ leg, legNumber: index + 1, ...legSpan(leg) }));

  let before: RebookingBar[];
  let after: RebookingBar[];
  const ghosts: { start: number; end: number }[] = [];

  if (fill) {
    const donors = new Set(fill.donorAssignmentIds);
    const receivers = new Set(
      fill.receiverAssignmentIds.length > 0
        ? fill.receiverAssignmentIds
        : legs.filter((l) => !donors.has(l.leg.assignmentId)).map((l) => l.leg.assignmentId)
    );
    after = legs.map(({ leg, legNumber, start, end }) => {
      const moved = byAssignment.get(leg.assignmentId);
      if (moved) ghosts.push({ start: start - shiftMs(moved), end: end - shiftMs(moved) });
      return {
        assignmentId: leg.assignmentId,
        legNumber,
        start,
        end,
        tone: moved ? 'moved' : donors.has(leg.assignmentId) ? 'donor' : 'own',
      };
    });
    before = legs
      .filter(({ leg }) => receivers.has(leg.assignmentId))
      .map(({ leg, legNumber, start, end }) => {
        const moved = byAssignment.get(leg.assignmentId);
        const back = moved ? shiftMs(moved) : 0;
        return {
          assignmentId: leg.assignmentId,
          legNumber,
          start: start - back,
          end: end - back,
          tone: 'own',
        };
      });
  } else {
    before = legs.map(({ leg, legNumber, start, end }) => ({
      assignmentId: leg.assignmentId,
      legNumber,
      start,
      end,
      tone: 'own',
    }));
    after = legs.map(({ leg, legNumber, start, end }) => {
      const moved = byAssignment.get(leg.assignmentId);
      if (!moved) {
        return { assignmentId: leg.assignmentId, legNumber, start, end, tone: 'own' };
      }
      ghosts.push({ start, end });
      return {
        assignmentId: leg.assignmentId,
        legNumber,
        start: start + shiftMs(moved),
        end: end + shiftMs(moved),
        tone: 'moved',
      };
    });
  }

  const stamps = [...before, ...after, ...ghosts].flatMap((b) => [b.start, b.end]);
  const dayStart = new Date(legs[0].start);
  dayStart.setHours(0, 0, 0, 0);
  const dayStartMs = dayStart.getTime();
  const hourOf = (ms: number) => (ms - dayStartMs) / 3_600_000;
  const axisStartHour = Math.floor(Math.min(...stamps.map(hourOf)));
  let axisEndHour = Math.ceil(Math.max(...stamps.map(hourOf)));
  // En kort dag ska inte se lika lång ut som en hel: minst sex timmar på axeln.
  axisEndHour = Math.max(axisEndHour, axisStartHour + 6);
  // Etiketterna fördelas jämnt över bredden, så steget måste gå jämnt upp i axeln.
  const tickStepHours = axisEndHour - axisStartHour > 10 ? 2 : 1;
  if ((axisEndHour - axisStartHour) % tickStepHours !== 0) axisEndHour += 1;

  const headline = fill ?? proposals[0];
  return {
    before,
    after,
    ghosts,
    hoursBefore: headline.dayHoursBefore,
    hoursAfter: headline.dayHoursAfter,
    dayStartMs,
    axisStartHour,
    axisEndHour,
    tickStepHours,
  };
};

/** Andel av axeln, 0–100, för en tidpunkt i millisekunder. */
export const timelinePercent = (
  ms: number,
  timeline: Pick<RebookingTimeline, 'dayStartMs' | 'axisStartHour' | 'axisEndHour'>
): number => {
  const hour = (ms - timeline.dayStartMs) / 3_600_000;
  const pct =
    ((hour - timeline.axisStartHour) / (timeline.axisEndHour - timeline.axisStartHour)) * 100;
  return Math.min(100, Math.max(0, pct));
};

/** Axelns etiketter som klockslag ("22", "00", "02"). */
export const timelineTicks = (
  timeline: Pick<RebookingTimeline, 'axisStartHour' | 'axisEndHour' | 'tickStepHours'>
): string[] => {
  const ticks: string[] = [];
  for (let h = timeline.axisStartHour; h <= timeline.axisEndHour; h += timeline.tickStepHours) {
    ticks.push((((h % 24) + 24) % 24).toString().padStart(2, '0'));
  }
  return ticks;
};
